import React, { useState, useEffect, useImperativeHandle, useCallback, forwardRef } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import JsonTreeViewer from './JsonTreeViewer';
import '../shared.css';
import './CustomAttributesManager.css';

export interface CustomAttributesManagerHandle {
  isEditing: boolean;
  saving: boolean;
  startEdit: () => void;
  cancelEdit: () => void;
  saveEdit: () => void;
}

interface CustomAttributesManagerProps {
  kgId: string;
  versionId: string;
  onEditingChange?: (editing: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

interface SchemaInfo {
  nodes: Set<string>;
  edges: Set<string>;
  nodeProperties: Map<string, Set<string>>;
  edgeProperties: Map<string, Set<string>>;
}

const CustomAttributesManager = forwardRef<CustomAttributesManagerHandle, CustomAttributesManagerProps>(
  ({ kgId, versionId, onEditingChange, onSavingChange }, ref) => {
  const [attributes, setAttributes] = useState<Record<string, any>>({});
  const [editedAttributesText, setEditedAttributesText] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [schemaInfo, setSchemaInfo] = useState<SchemaInfo | null>(null);

  useEffect(() => {
    loadCustomAttributes();
    loadSchema();
  }, [kgId, versionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { onEditingChange?.(isEditing); }, [isEditing, onEditingChange]);
  useEffect(() => { onSavingChange?.(saving); }, [saving, onSavingChange]);

  const loadCustomAttributes = async () => {
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.getCustomAttributes(kgId, versionId);

      if (response.data) {
        const attrs = response.data.attributes?.custom_attributes || {};
        setAttributes(attrs);
        setEditedAttributesText(JSON.stringify(attrs, null, 2));
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load custom attributes'));
      console.error('Error loading custom attributes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSchema = async () => {
    try {
      const response: any = await apiService.getSchema(kgId, versionId);
      const schemaData = response.data?.attributes?.schema || response.data?.schema || response.data || {};

      const nodes = new Set<string>();
      const edges = new Set<string>();
      const nodeProperties = new Map<string, Set<string>>();
      const edgeProperties = new Map<string, Set<string>>();

      // Process nodes
      const nodeLabels = schemaData.nodes ? Object.keys(schemaData.nodes) : [];
      nodeLabels.forEach(label => {
        nodes.add(label);
        const nodeData = schemaData.nodes[label];
        const properties = new Set<string>();

        if (nodeData && typeof nodeData === 'object') {
          const propsObj = nodeData.properties || nodeData;
          Object.keys(propsObj).forEach(propName => {
            const propData = propsObj[propName];
            if (typeof propData === 'object' && propData.type) {
              properties.add(propName);
            }
          });
        }

        nodeProperties.set(label, properties);
      });

      // Process edges
      const edgesData = schemaData.edges || schemaData.relationships || {};
      Object.keys(edgesData).forEach(edgeType => {
        edges.add(edgeType);
        const edgeData = edgesData[edgeType];
        const properties = new Set<string>();

        if (edgeData && typeof edgeData === 'object') {
          const propsObj = edgeData.properties || edgeData;
          Object.keys(propsObj).forEach(propName => {
            const propData = propsObj[propName];
            if (typeof propData === 'object' && propData.type) {
              properties.add(propName);
            }
          });
        }

        edgeProperties.set(edgeType, properties);
      });

      setSchemaInfo({ nodes, edges, nodeProperties, edgeProperties });
    } catch (err: any) {
      console.error('Error loading schema:', err);
      // Don't show error to user, schema validation is optional
    }
  };

  const handleSave = useCallback(async () => {
    setError('');
    setSuccess('');

    try {
      const parsedAttributes = JSON.parse(editedAttributesText);
      setSaving(true);
      await apiService.updateCustomAttributes(kgId, versionId, parsedAttributes);
      setSuccess('Custom attributes updated successfully');
      setAttributes(parsedAttributes);
      setIsEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format');
      } else {
        setError(extractErrorMessage(err, 'Failed to update custom attributes'));
      }
      console.error('Error updating custom attributes:', err);
    } finally {
      setSaving(false);
    }
  }, [kgId, versionId, editedAttributesText]);

  const handleCancel = useCallback(() => {
    setEditedAttributesText(JSON.stringify(attributes, null, 2));
    setIsEditing(false);
    setError('');
  }, [attributes]);

  useImperativeHandle(ref, () => ({
    isEditing,
    saving,
    startEdit:  () => setIsEditing(true),
    cancelEdit: handleCancel,
    saveEdit:   handleSave,
  }), [isEditing, saving, handleSave, handleCancel]);

  if (loading) {
    return (
      <div className="custom-attributes-manager tab-root">
        <div className="tab-loader">
          <span className="tab-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="custom-attributes-manager tab-root">
      <div className="cam-toolbar">
        {saving && <span className="cam-saving-label">Saving…</span>}
        {!isEditing ? (
          <button className="tab-btn tab-btn--primary" onClick={() => setIsEditing(true)}>
            Edit Attributes
          </button>
        ) : (
          <>
            <button className="tab-btn tab-btn--ghost" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
            <button className="tab-btn tab-btn--primary" onClick={handleSave} disabled={saving}>
              Save Changes
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
        </div>
      )}
      {success && (
        <div className="tab-banner tab-banner--success">
          <span className="tab-banner-body">{success}</span>
        </div>
      )}

      <div className="attributes-content">
        {isEditing ? (
          <textarea
            value={editedAttributesText}
            onChange={(e) => setEditedAttributesText(e.target.value)}
            className="attributes-textarea"
            placeholder='{\n  "key": "value"\n}'
            spellCheck={false}
          />
        ) : (
          Object.keys(attributes).length > 0 ? (
            <JsonTreeViewer data={attributes} isRoot={true} schemaInfo={schemaInfo} />
          ) : (
            <div className="tab-empty">
              <div className="tab-empty-icon">{'{}'}</div>
              <div className="tab-empty-title">No custom attributes defined</div>
              <div className="tab-empty-body">Click "Edit Attributes" to add metadata for this version.</div>
            </div>
          )
        )}
      </div>
    </div>
  );
});

CustomAttributesManager.displayName = 'CustomAttributesManager';
export default CustomAttributesManager;
